import React, { useState, useEffect } from 'react';
import api from '../services/api';

function Templates() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('business');

  // Line Item Templates state
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '', items: [] });
  const [templateSaving, setTemplateSaving] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (activeTab === 'templates') {
      fetchTemplates();
    }
  }, [activeTab]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const data = await api.getProfile();
      setProfile(data);
    } catch (err) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setProfile({ ...profile, [field]: value });
    setSuccess('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await api.updateProfile(profile);
      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Line Item Templates functions
  const fetchTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const data = await api.getLineItemTemplates();
      setTemplates(data);
    } catch (err) {
      setError(err.message || 'Failed to load templates');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const openAddTemplateModal = () => {
    setEditingTemplate(null);
    setTemplateForm({ name: '', description: '', items: [] });
    setShowTemplateModal(true);
  };

  const openEditTemplateModal = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      description: template.description || '',
      items: template.items.map(item => ({ ...item })),
    });
    setShowTemplateModal(true);
  };

  const addTemplateItem = () => {
    setTemplateForm({
      ...templateForm,
      items: [...templateForm.items, { type: 'labor', description: '', hours: '', rate: '', cost: '', apply_tax: true }],
    });
  };

  const updateTemplateItem = (index, field, value) => {
    const newItems = [...templateForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setTemplateForm({ ...templateForm, items: newItems });
  };

  const removeTemplateItem = (index) => {
    setTemplateForm({
      ...templateForm,
      items: templateForm.items.filter((_, i) => i !== index),
    });
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim()) {
      setError('Template name is required');
      return;
    }

    setTemplateSaving(true);
    setError('');

    try {
      const data = {
        name: templateForm.name,
        description: templateForm.description,
        items: templateForm.items.map(item => ({
          type: item.type,
          description: item.description,
          hours: item.type === 'labor' || item.type === 'daily_labor' ? parseFloat(item.hours) || null : null,
          rate: item.type === 'labor' || item.type === 'daily_labor' ? parseFloat(item.rate) || null : null,
          cost: item.type === 'material' || item.type === 'other' ? parseFloat(item.cost) || null : null,
          apply_tax: item.apply_tax,
        })),
      };

      if (editingTemplate) {
        await api.updateLineItemTemplate(editingTemplate.id, data);
        setSuccess('Template updated successfully!');
      } else {
        await api.createLineItemTemplate(data);
        setSuccess('Template created successfully!');
      }

      setShowTemplateModal(false);
      fetchTemplates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save template');
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (!window.confirm(`Delete "${template.name}"? This cannot be undone.`)) return;

    try {
      await api.deleteLineItemTemplate(template.id);
      setSuccess('Template deleted');
      fetchTemplates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete template');
    }
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      await api.duplicateLineItemTemplate(template.id);
      setSuccess('Template duplicated');
      fetchTemplates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to duplicate template');
    }
  };

  if (loading) {
    return (
      <div className="py-20 px-6 bg-background min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
          <p className="text-slate-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'business', label: 'Business Info', icon: 'fa-building' },
    { id: 'documents', label: 'Document Settings', icon: 'fa-file-alt' },
    { id: 'payment', label: 'Payment Info', icon: 'fa-credit-card' },
    { id: 'templates', label: 'Line Item Templates', icon: 'fa-layer-group' },
  ];

  return (
    <div className="py-12 px-6 bg-background min-h-screen">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-black text-primary mb-2">Templates & Settings</h1>
          <p className="text-slate-600">Customize your quotes, invoices, and business information</p>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mb-6 bg-slate-100 p-1 rounded-xl">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium transition ${
                activeTab === tab.id
                  ? 'bg-white text-primary shadow-sm'
                  : 'text-slate-600 hover:text-primary'
              }`}
            >
              <i className={`fas ${tab.icon}`}></i>
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center">
            <i className="fas fa-exclamation-circle mr-2"></i>
            {error}
          </div>
        )}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-xl flex items-center">
            <i className="fas fa-check-circle mr-2"></i>
            {success}
          </div>
        )}

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-8">
          {activeTab === 'business' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-primary mb-4">Business Information</h2>
              <p className="text-slate-600 text-sm mb-6">
                This information appears on your quotes and invoices.
              </p>

              <div className="grid gap-6 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-slate-700 mb-2">Business Name</label>
                  <input
                    type="text"
                    value={profile?.business_name || ''}
                    onChange={(e) => handleChange('business_name', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                    placeholder="Your Company Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Business Email</label>
                  <input
                    type="email"
                    value={profile?.business_email || ''}
                    onChange={(e) => handleChange('business_email', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                    placeholder="contact@company.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={profile?.phone || ''}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Website</label>
                  <input
                    type="text"
                    value={profile?.business_website || ''}
                    onChange={(e) => handleChange('business_website', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                    placeholder="www.company.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Tax ID / VAT Number</label>
                  <input
                    type="text"
                    value={profile?.tax_id_number || ''}
                    onChange={(e) => handleChange('tax_id_number', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                    placeholder="XX-XXXXXXX"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-slate-700 mb-2">Street Address</label>
                  <input
                    type="text"
                    value={profile?.address_street || ''}
                    onChange={(e) => handleChange('address_street', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                    placeholder="123 Main Street"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">City</label>
                  <input
                    type="text"
                    value={profile?.address_city || ''}
                    onChange={(e) => handleChange('address_city', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                    placeholder="City"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">State / Region</label>
                  <input
                    type="text"
                    value={profile?.address_state || ''}
                    onChange={(e) => handleChange('address_state', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                    placeholder="State"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Postal Code</label>
                  <input
                    type="text"
                    value={profile?.address_zip || ''}
                    onChange={(e) => handleChange('address_zip', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                    placeholder="12345"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-primary mb-4">Document Settings</h2>
              <p className="text-slate-600 text-sm mb-6">
                Configure defaults for your quotes and invoices.
              </p>

              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Default Currency</label>
                  <select
                    value={profile?.preferred_currency || '$'}
                    onChange={(e) => handleChange('preferred_currency', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition bg-white"
                  >
                    <option value="$">USD ($)</option>
                    <option value="£">GBP (£)</option>
                    <option value="€">EUR (€)</option>
                    <option value="A$">AUD (A$)</option>
                    <option value="C$">CAD (C$)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Default Hourly Rate</label>
                  <input
                    type="number"
                    step="0.01"
                    value={profile?.default_hourly_rate || ''}
                    onChange={(e) => handleChange('default_hourly_rate', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                    placeholder="75.00"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={profile?.tax_enabled || false}
                      onChange={(e) => handleChange('tax_enabled', e.target.checked)}
                      className="w-5 h-5 text-secondary border-slate-300 rounded focus:ring-secondary"
                    />
                    <span className="text-sm font-bold text-slate-700">Enable Tax on Documents</span>
                  </label>
                </div>

                {profile?.tax_enabled && (
                  <>
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Tax Type</label>
                      <select
                        value={profile?.tax_type || 'VAT'}
                        onChange={(e) => handleChange('tax_type', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition bg-white"
                      >
                        <option value="VAT">VAT</option>
                        <option value="Sales Tax">Sales Tax</option>
                        <option value="GST">GST</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Default Tax Rate (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={profile?.default_tax_rate || ''}
                        onChange={(e) => handleChange('default_tax_rate', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                        placeholder="20.00"
                      />
                    </div>
                  </>
                )}

                <div className="md:col-span-2 pt-4 border-t border-slate-200">
                  <h3 className="font-bold text-slate-700 mb-4">PDF Display Options</h3>
                  <div className="space-y-3">
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={profile?.show_tax_id_on_pdf || false}
                        onChange={(e) => handleChange('show_tax_id_on_pdf', e.target.checked)}
                        className="w-5 h-5 text-secondary border-slate-300 rounded focus:ring-secondary"
                      />
                      <span className="text-sm text-slate-700">Show Tax ID on PDFs</span>
                    </label>
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={profile?.show_bank_details_on_pdf || false}
                        onChange={(e) => handleChange('show_bank_details_on_pdf', e.target.checked)}
                        className="w-5 h-5 text-secondary border-slate-300 rounded focus:ring-secondary"
                      />
                      <span className="text-sm text-slate-700">Show Bank Details on PDFs</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'payment' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-primary mb-4">Payment Information</h2>
              <p className="text-slate-600 text-sm mb-6">
                Add your bank details to display on invoices (optional).
              </p>

              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Bank Name</label>
                  <input
                    type="text"
                    value={profile?.bank_name || ''}
                    onChange={(e) => handleChange('bank_name', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                    placeholder="Bank of America"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Account Holder Name</label>
                  <input
                    type="text"
                    value={profile?.bank_account_holder || ''}
                    onChange={(e) => handleChange('bank_account_holder', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                    placeholder="Your Company LLC"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Account Number</label>
                  <input
                    type="text"
                    value={profile?.bank_account_number || ''}
                    onChange={(e) => handleChange('bank_account_number', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                    placeholder="****1234"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Routing Number / Sort Code</label>
                  <input
                    type="text"
                    value={profile?.bank_routing_number || ''}
                    onChange={(e) => handleChange('bank_routing_number', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                    placeholder="123456789"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-slate-700 mb-2">Payment Instructions</label>
                  <textarea
                    value={profile?.payment_instructions || ''}
                    onChange={(e) => handleChange('payment_instructions', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition resize-none"
                    placeholder="Payment due within 30 days. Please include invoice number with payment."
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'templates' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-primary">Line Item Templates</h2>
                  <p className="text-slate-600 text-sm mt-1">
                    Save common line items as templates for quick reuse in quotes and invoices.
                  </p>
                </div>
                <button
                  onClick={openAddTemplateModal}
                  className="px-4 py-2 bg-secondary text-white rounded-xl font-bold hover:bg-orange-600 transition shadow-lg"
                >
                  <i className="fas fa-plus mr-2"></i>
                  New Template
                </button>
              </div>

              {templatesLoading ? (
                <div className="text-center py-12">
                  <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
                  <p className="text-slate-600">Loading templates...</p>
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-xl">
                  <i className="fas fa-layer-group text-5xl text-slate-300 mb-4"></i>
                  <h3 className="text-lg font-bold text-slate-700 mb-2">No Templates Yet</h3>
                  <p className="text-slate-500 mb-4">
                    Create templates for common line items like "Standard Bathroom Remodel" or "Electrical Rough-In"
                  </p>
                  <button
                    onClick={openAddTemplateModal}
                    className="px-6 py-2 bg-secondary text-white rounded-xl font-bold hover:bg-orange-600 transition"
                  >
                    <i className="fas fa-plus mr-2"></i>
                    Create Your First Template
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      className="border border-slate-200 rounded-xl p-4 hover:border-secondary transition"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-bold text-primary text-lg">{template.name}</h3>
                          {template.description && (
                            <p className="text-slate-500 text-sm mt-1">{template.description}</p>
                          )}
                          <div className="flex items-center space-x-4 mt-2 text-sm text-slate-500">
                            <span>
                              <i className="fas fa-list-ul mr-1"></i>
                              {template.item_count} item{template.item_count !== 1 ? 's' : ''}
                            </span>
                            <span>
                              <i className="fas fa-calculator mr-1"></i>
                              {profile?.preferred_currency || '$'}
                              {parseFloat(template.total_estimate || 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openEditTemplateModal(template)}
                            className="p-2 text-slate-500 hover:text-primary hover:bg-slate-100 rounded-lg transition"
                            title="Edit"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            onClick={() => handleDuplicateTemplate(template)}
                            className="p-2 text-slate-500 hover:text-secondary hover:bg-slate-100 rounded-lg transition"
                            title="Duplicate"
                          >
                            <i className="fas fa-copy"></i>
                          </button>
                          <button
                            onClick={() => handleDeleteTemplate(template)}
                            className="p-2 text-slate-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                            title="Delete"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Save Button - only show on settings tabs */}
          {activeTab !== 'templates' && (
          <div className="mt-8 pt-6 border-t border-slate-200">
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full md:w-auto px-8 py-3 bg-secondary text-white rounded-xl font-bold hover:bg-orange-600 transition shadow-lg disabled:opacity-50"
            >
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save mr-2"></i>
                  Save Changes
                </>
              )}
            </button>
          </div>
          )}
        </div>
      </div>

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-primary">
                  {editingTemplate ? 'Edit Template' : 'New Template'}
                </h2>
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Template Name *</label>
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                  placeholder="e.g., Standard Bathroom Remodel"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Description (Optional)</label>
                <textarea
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition resize-none"
                  placeholder="Brief description of what this template includes"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-bold text-slate-700">Line Items</label>
                  <button
                    onClick={addTemplateItem}
                    className="px-3 py-1 text-sm bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition"
                  >
                    <i className="fas fa-plus mr-1"></i>
                    Add Item
                  </button>
                </div>

                {templateForm.items.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                    <i className="fas fa-list text-3xl text-slate-300 mb-2"></i>
                    <p className="text-slate-500 text-sm">No items yet. Add line items to this template.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {templateForm.items.map((item, index) => (
                      <div key={index} className="border border-slate-200 rounded-xl p-4 bg-slate-50">
                        <div className="flex items-start justify-between mb-3">
                          <span className="text-xs font-bold text-slate-400 uppercase">Item {index + 1}</span>
                          <button
                            onClick={() => removeTemplateItem(index)}
                            className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                          >
                            <i className="fas fa-trash text-xs"></i>
                          </button>
                        </div>

                        <div className="grid gap-3 md:grid-cols-2">
                          <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-slate-600 mb-1">Type</label>
                            <select
                              value={item.type}
                              onChange={(e) => updateTemplateItem(index, 'type', e.target.value)}
                              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-secondary focus:outline-none text-sm bg-white"
                            >
                              <option value="labor">Hourly Labor</option>
                              <option value="daily_labor">Daily Labor</option>
                              <option value="material">Material</option>
                              <option value="other">Other Cost</option>
                            </select>
                          </div>

                          <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) => updateTemplateItem(index, 'description', e.target.value)}
                              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-secondary focus:outline-none text-sm"
                              placeholder="Item description"
                            />
                          </div>

                          {(item.type === 'labor' || item.type === 'daily_labor') ? (
                            <>
                              <div>
                                <label className="block text-xs font-medium text-slate-600 mb-1">
                                  {item.type === 'daily_labor' ? 'Days' : 'Hours'}
                                </label>
                                <input
                                  type="number"
                                  step="0.5"
                                  value={item.hours || ''}
                                  onChange={(e) => updateTemplateItem(index, 'hours', e.target.value)}
                                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-secondary focus:outline-none text-sm"
                                  placeholder="0"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-slate-600 mb-1">Rate</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={item.rate || ''}
                                  onChange={(e) => updateTemplateItem(index, 'rate', e.target.value)}
                                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-secondary focus:outline-none text-sm"
                                  placeholder={profile?.default_hourly_rate || '0.00'}
                                />
                              </div>
                            </>
                          ) : (
                            <div className="md:col-span-2">
                              <label className="block text-xs font-medium text-slate-600 mb-1">Cost</label>
                              <input
                                type="number"
                                step="0.01"
                                value={item.cost || ''}
                                onChange={(e) => updateTemplateItem(index, 'cost', e.target.value)}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-secondary focus:outline-none text-sm"
                                placeholder="0.00"
                              />
                            </div>
                          )}

                          <div className="md:col-span-2">
                            <label className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={item.apply_tax}
                                onChange={(e) => updateTemplateItem(index, 'apply_tax', e.target.checked)}
                                className="w-4 h-4 text-secondary border-slate-300 rounded focus:ring-secondary"
                              />
                              <span className="text-xs text-slate-600">Apply tax to this item</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowTemplateModal(false)}
                className="px-6 py-2 text-slate-600 hover:text-slate-800 font-medium transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTemplate}
                disabled={templateSaving}
                className="px-6 py-2 bg-secondary text-white rounded-xl font-bold hover:bg-orange-600 transition shadow-lg disabled:opacity-50"
              >
                {templateSaving ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-2"></i>
                    {editingTemplate ? 'Update Template' : 'Save Template'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Templates;
